import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import axiosInstance from "utils/request";
import { GoogleLoginButton } from "./GoogleLoginButton";
import t from "./translations";

const SSOConfigSchema = z.array(
  z.object({
    provider: z.string(),
    internal: z.boolean(),
    googleData: z
      .object({ id: z.number(), clientId: z.string() })
      .optional(),
  })
);

type SSOConfig = z.infer<typeof SSOConfigSchema>[number];

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [ssoConfigs, setSsoConfigs] = useState<SSOConfig[]>([]);

  const handleLogin = () => {
    const redirect = searchParams.get("redirect");
    navigate(redirect ? decodeURIComponent(redirect) : "/");
  };

  useEffect(() => {
    let mounted = true;
    async function fetchSSOConfigs() {
      try {
        const { data } = await axiosInstance.get("/auth/oauth");
        if (mounted) setSsoConfigs(SSOConfigSchema.parse(data));
      } catch {
        if (mounted) setSsoConfigs([]);
      }
    }
    fetchSSOConfigs();
    return () => {
      mounted = false;
    };
  }, []);

  const googleConfigs = ssoConfigs.filter(
    (c) => c.provider === "google" && c.googleData
  );

  return (
    <Container component="main" maxWidth="xs" sx={{ height: "100vh", display: "flex", alignItems: "center" }}>
      <Paper sx={{ p: 4, width: "100%" }}>
        <Typography component="h1" variant="h5" gutterBottom>
          {t.signIn}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {googleConfigs.map((config) => (
            <GoogleOAuthProvider key={config.googleData!.id} clientId={config.googleData!.clientId}>
              <GoogleLoginButton
                configId={config.googleData!.id}
                onLogin={handleLogin}
                onError={setError}
              />
            </GoogleOAuthProvider>
          ))}
        </Box>

        <Stack sx={{ mt: 2, alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Arkion
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}
